import {
  DataTable,
  DataTableFooter,
  DataTableHeader,
  DataTablePagination,
  DataTableSearch,
  DataTableTitle,
  useTableState,
} from '@crehana/compass.ui/v3/table';
import { useState } from 'react';
import {
  TableErrorState,
  TableNoDataState,
  TableNoResultsState,
} from '@/shared/components/table-state';
import { DailyBreakdownTable } from './components/daily-breakdown-table';
import { DateFilterBar } from './components/date-filter-bar';
import { SummaryCards } from './components/summary-cards';
import { useCollaboratorsColumns } from './components/use-collaborators-columns';
import { useHoursCalculation } from './hooks/use-hours-calculation';
import { useTranslation } from './i18n';

function getDefaultDateFrom(): Date {
  const now = new Date();

  return new Date(now.getFullYear(), now.getMonth() - 1, 1);
}

function getDefaultDateTo(): Date {
  return new Date();
}

export const HoursCalculationContent = () => {
  const { t } = useTranslation();

  const [pickerDateFrom, setPickerDateFrom] = useState<Date | undefined>(
    getDefaultDateFrom,
  );
  const [pickerDateTo, setPickerDateTo] = useState<Date | undefined>(
    getDefaultDateTo,
  );

  const [appliedDateFrom, setAppliedDateFrom] = useState<Date | undefined>(
    getDefaultDateFrom,
  );
  const [appliedDateTo, setAppliedDateTo] = useState<Date | undefined>(
    getDefaultDateTo,
  );

  const { summary, collaborators, loading, error, refetch, totalCount } =
    useHoursCalculation({
      dateFrom: appliedDateFrom,
      dateTo: appliedDateTo,
    });

  const columns = useCollaboratorsColumns();
  const tableState = useTableState({ search: '' });

  const isDateRangeValid =
    pickerDateFrom && pickerDateTo && pickerDateTo >= pickerDateFrom;

  const handleApplyFilter = () => {
    if (!isDateRangeValid) {
      return;
    }

    setAppliedDateFrom(pickerDateFrom);
    setAppliedDateTo(pickerDateTo);
  };

  const getTableEmptyState = () => {
    if (error) {
      return (
        <TableErrorState
          onClickButton={() => refetch()}
          title={t('TABLE_STATE_ERROR_TITLE')}
          description={t('TABLE_STATE_ERROR_SUBTITLE')}
          buttonLabel={t('TABLE_STATE_ERROR_RETRY')}
        />
      );
    }
    if (tableState.search.trim() !== '' && collaborators.length === 0) {
      return (
        <TableNoResultsState
          highlightedText={tableState.search}
          title={t('TABLE_STATE_EMPTY_SEARCH_TITLE')}
          description={t('TABLE_STATE_EMPTY_SEARCH_SUBTITLE')}
        />
      );
    }
    return (
      <TableNoDataState
        buttonLabel={t('TABLE_STATE_EMPTY_NO_DATA_CTA')}
        title={t('TABLE_STATE_EMPTY_NO_DATA_TITLE')}
        description={t('TABLE_STATE_EMPTY_NO_DATA_SUBTITLE')}
      />
    );
  };

  return (
    <div className="tw-flex tw-flex-col tw-gap-2.4">
      <DateFilterBar
        dateFrom={pickerDateFrom}
        dateTo={pickerDateTo}
        appliedDateFrom={appliedDateFrom}
        appliedDateTo={appliedDateTo}
        onDateFromChange={setPickerDateFrom}
        onDateToChange={setPickerDateTo}
        onApplyFilter={handleApplyFilter}
        disabled={loading}
        filterDisabled={!isDateRangeValid}
      />

      <SummaryCards summary={error ? undefined : summary} loading={loading} />

      <DataTable
        columns={columns}
        data={collaborators}
        rowCount={totalCount}
        state={tableState}
        loading={loading}
        locale="es"
        expandedContent={row => (
          <DailyBreakdownTable collaborator={row} days={row.dailyDetails} />
        )}
        emptyState={getTableEmptyState()}
        header={
          <DataTableHeader>
            <DataTableTitle count={totalCount}>
              {t('TABLE_TITLE')}
            </DataTableTitle>
            <DataTableSearch placeholder={t('SEARCH_PLACEHOLDER')} />
          </DataTableHeader>
        }
        footer={
          <DataTableFooter>
            <DataTablePagination />
          </DataTableFooter>
        }
      />
    </div>
  );
};
